'use client'

import { useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  TrashIcon,
  FileTextIcon,
  HashIcon,
  WaypointsIcon,
  CalculatorIcon,
  MessageCircleMoreIcon,
  FunnelIcon,
  RefreshCwIcon,
  CopyIcon
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { CodeBlock } from '@/components/ui/code-block'
import {
  ConfirmationDialog,
  ContentDetailInfo,
  ContentDetailFile,
  ContentError,
  DeleteConfirmationDialog,
  JobStatusBadge
} from '@/components/partials'
import {
  useProductExcelExport,
  useDeleteProductExcelExport,
  useCreateProductExcelExport,
  useCopyToClipboard
} from '@/hooks'
import { formatNumber, tOptional } from '@/lib/utils'

type PropsType = {
  id: string
}

const ProductExcelExportShowSkeleton = () => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <Skeleton className="h-9 w-16" />
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
      </div>
      <Skeleton className="h-9 w-20" />
    </div>
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-48" />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Skeleton className="h-4 w-8" />
            <Skeleton className="h-6 w-32" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-6 w-24" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-36" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-36" />
          </div>
        </div>
      </CardContent>
    </Card>
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-4 w-64" />
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex gap-2">
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-24" />
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-20 w-full" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
)

const Detail = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const locale = useLocale()
  const queryClient = useQueryClient()
  const { copyToClipboard } = useCopyToClipboard()
  const [deleteProductExcelExportId, setProductExcelExportId] = useState<string | null>(null)

  const { data: productExcelExport, isLoading, error } = useProductExcelExport(id)
  const { mutate: deleteProductExcelExport, isPending: isDeleting } = useDeleteProductExcelExport()
  const { mutate: createProductExcelExportMutate, isPending: isRegenerating } = useCreateProductExcelExport()

  const handleDelete = () => {
    setProductExcelExportId(id)
  }

  const [isRegenerateConfirmOpen, setIsRegenerateConfirmOpen] = useState(false)

  const handleRegenerate = () => {
    if (productExcelExport?.filters) {
      createProductExcelExportMutate(productExcelExport.filters, {
        onSuccess: async (newExport: any) => {
          await queryClient.invalidateQueries({ queryKey: ['productExcelExports'] })
          toast.success(t('created_successfully'))
          // Navigate to the new export's detail page
          router.push(`/products/export/excel/${newExport.id}`)
        },
        onError: (error: any) => {
          console.error('Error regenerating productExcelExport:', error)
          toast.error(t('error_creating'))
        }
      })
    }
  }

  const confirmDeleteProductExcelExport = () => {
    if (deleteProductExcelExportId) {
      deleteProductExcelExport(deleteProductExcelExportId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['productExcelExports'] })
          toast.success(t('deleted_successfully'))
          router.push('/products/export/excel')
          setProductExcelExportId(null)
        },
        onError: error => {
          console.error('Error deleting productExcelExport:', error)
          toast.error(t('error_deleting'))
          setProductExcelExportId(null)
        }
      })
    }
  }

  if (isLoading || isDeleting) {
    return <ProductExcelExportShowSkeleton />
  }

  if (error || !productExcelExport) {
    return <ContentError error={error} listUrl="products/export/excel" />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="grid grid-cols-1 items-center justify-between gap-2 md:grid-cols-2">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => router.push('/products/export/excel')}>
            <ArrowLeftIcon className="h-4 w-4" />
            {t('list')}
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('product_excel_export_details')}</h1>
          </div>
        </div>
        <div className="flex flex-row items-center justify-end gap-2">
          <Button variant="outline" onClick={() => setIsRegenerateConfirmOpen(true)} disabled={isRegenerating}>
            <RefreshCwIcon className="h-4 w-4" />
            <span className="hidden items-center gap-2 lg:flex">{t('regenerate')}</span>
          </Button>
          <Button variant="destructive" onClick={handleDelete}>
            <TrashIcon className="h-4 w-4" />
            <span className="hidden items-center gap-2 lg:flex">{t('delete')}</span>
          </Button>
        </div>
      </div>

      {/* Blog Post Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileTextIcon className="h-5 w-5" />
            {t('information')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-4">
            <div className="col-span-4 space-y-2 xl:col-span-1">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <HashIcon className="h-4 w-4" />
                ID
              </div>
              <div className="flex items-center gap-1">
                <Badge variant="outline" className="font-mono text-xs">
                  {productExcelExport.id}
                </Badge>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(productExcelExport.id)}
                      className="h-6 w-6 p-0">
                      <CopyIcon className="h-3 w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('copy')}</TooltipContent>
                </Tooltip>
              </div>
            </div>
            <div className="col-span-4 space-y-2 xl:col-span-1">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <CalculatorIcon className="h-4 w-4" />
                {t('total')}
              </div>
              <Badge variant="outline" className="font-mono text-xs">
                {formatNumber(productExcelExport.total, locale)}
              </Badge>
            </div>
            <div className="col-span-4 space-y-2 xl:col-span-1">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <WaypointsIcon className="h-4 w-4" />
                {t('status')}
              </div>
              <JobStatusBadge status={productExcelExport.status} />
            </div>
            <div className="col-span-4 space-y-2 xl:col-span-1">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <MessageCircleMoreIcon className="h-4 w-4" />
                {t('message')}
              </div>
              <Badge variant="outline" className="block font-mono text-xs break-all whitespace-normal">
                {tOptional(t, productExcelExport.message ?? '-')}
              </Badge>
            </div>
            <div className="col-span-4 space-y-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <FunnelIcon className="h-4 w-4" />
                {t('filters')}
              </div>
              {productExcelExport.filters ? (
                <CodeBlock code={JSON.stringify(productExcelExport.filters, null, 2)} language="json" />
              ) : (
                <Badge variant="outline" className="font-mono text-xs">
                  -
                </Badge>
              )}
            </div>
          </div>
          <Separator />
          <ContentDetailInfo entity={productExcelExport} />
        </CardContent>
      </Card>

      {/* Files */}
      {productExcelExport.file && <ContentDetailFile file={productExcelExport.file} />}

      <ConfirmationDialog
        open={isRegenerateConfirmOpen}
        onCancel={() => setIsRegenerateConfirmOpen(false)}
        onConfirm={() => {
          setIsRegenerateConfirmOpen(false)
          handleRegenerate()
        }}
        description={t('regenerate_export_confirmation')}
        confirmText={t('regenerate')}
        isLoading={isRegenerating}
      />

      <DeleteConfirmationDialog
        open={deleteProductExcelExportId !== null}
        onCancel={() => setProductExcelExportId(null)}
        onConfirm={confirmDeleteProductExcelExport}
        isDeleting={isDeleting}
      />
    </div>
  )
}

export default Detail
